import { Block, Content, Flex, Loader, Title } from '@/common/components';
import { Meta } from '@/common/components/head';
import { createPage, For, If, Slot } from '@/common/util/templateHelpers';
import ProductFeature from '@/components/ProductFeature';
import ProductPlanBox from '@/components/ProductPlanBox';
import DefaultLayout from '@/layouts/DefaultLayout';
import { useModal } from '@/common/hooks/modal';
import userApi from '@/apis/drupal/user';

import { useAuthenticated } from '@/common/hooks/authenticated';

import style from './index.module.scss';
import { useEffect, useState } from 'react';
import { useThemeStore } from '@/stores/theme';
import Script from 'next/script';

interface Plan {
  name: string
  price: string
  description: string
  code: string
}

interface GamePassPageProps {
  plans: Plan[]
}

export async function getStaticProps () {
  // TODO: fetch these from Recurly
  const plans = [
    {
      name: 'Annual Subscription',
      price: '2.50',
      description: 'Paid once per year',
      code: 'annual'
    },
    {
      name: 'Monthly Subscription',
      price: '3.99',
      description: 'Paid each month',
      code: 'monthly'
    },
  ];

  return {
    props: {
      plans
    },
    revalidate: process.env.APP_CONFIG.REVALIDATE_INTERVAL_MEDIUM
  };
}

export default createPage<GamePassPageProps>('GamePassPage', 
  { style }, 
  function  ({ className }, props) {
    const { openModal } = useModal();

    const openRegisterModal = () => {
      openModal('register');
    };

    const [ currentSubscription, setCurrentSubscription ] = useState<null|'monthly'|'annual'>(null);
    const [ isLoading, setIsLoading ] = useState(true);
    const [ themeState ] = useThemeStore();

    const { isLoggedIn, state: authState } = useAuthenticated({});

    const getUserPlan = async () =>{
      setIsLoading(true);

      if(authState?.user?.hasGamePass){
        const userPlan = await userApi.plan(authState.token);
        setCurrentSubscription(userPlan.user_plan);
      }

      setIsLoading(false);
    };

    useEffect(() => {
      if (isLoggedIn) {
        setIsLoading(() => false);
        return;
      }
    }, [ isLoggedIn ]);

    useEffect(() => {
      if(authState.ready && isLoggedIn || authState?.user?.hasGamePass) {
        getUserPlan();
        return;
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ authState.ready, isLoggedIn, authState?.user?.hasGamePass ]);

    const changeSubscription = (plan) => {
      openModal('gamePassUpdate', { onClose () {
        getUserPlan();
      } }, plan);
    };

    return (
      <DefaultLayout className={className}>
        <Meta 
          titleTag='Game Pass | Addicting Games'
          title='Game Pass'
          openGraph={
            {
              url: process.env.APP_CONFIG.URL + '/game-pass'
            }
          }
          sitemapProps={{ priority: '0.5' }}
        />

        <Block>
          <Content large className='--textCenter'>
            {
              If(themeState.theme === 'default', () => (
                <img key='defaultGamePassLogo' src='/images/gamePass.svg' alt='GamePass' width='400px' />
              ))
                .Else(() => (
                  <img key='classicGamePassLogo' src='/images/gamePassClassic.svg' alt='GamePass' width='400px' />
                ))
                .EndIf()
            }
            <p>Remove ads and get to games faster with GamePass!</p>
          </Content>
        </Block>
    
        <br />
        { 
          If(isLoading, ()=>(
            <Flex alignCenter>
              <Loader large />
            </Flex>
          )).Else(()=>(
            <Block>
              <Flex gap1 container collapseMobile>
                {
                  If(!isLoggedIn, () => (
                    <>
                      <Flex fit>
                        <ProductPlanBox disabled={isLoggedIn} buttonOnClick={openRegisterModal}>
                          <Slot name='title'>Free</Slot>
                          <Slot name='cost'>$0 / month</Slot>
                          <Slot name='description'>Upgrade whenever you want.<br /> Free access to all our base features.</Slot>
                          <Slot name='buttonText'>{isLoggedIn ? 'Already Joined ✓' : 'Join'}</Slot>
                        </ProductPlanBox>
                      </Flex>
                      {
                        For(props.plans, (plan) => (
                          <Flex key={plan.name} fit>
                            <ProductPlanBox buttonHref={ `/subscribe/${plan.code}`} featured={plan.code === 'annual' ? true : false}>
                              <Slot name='title'>{plan.name}</Slot>
                            
                              {
                                If(plan.code === 'annual', () => (
                                  <Slot name='sticker'>Best Value!</Slot>
                                )).EndIf()
                              }
                              <Slot name='cost'>${plan.price} / Month</Slot>
                              <Slot name='description'>{plan.description}</Slot>
                              <Slot name='buttonText'>Start Subscription</Slot>
                            </ProductPlanBox>
                          </Flex> 
                        ))
                      }
                    </>
                  )).ElseIf(authState?.user?.hasGamePass,() => (
                    <>
                      {
                        For(props.plans, (plan) => (
                          <Flex key={plan.name} fit>
                            <ProductPlanBox disabled={currentSubscription ===  plan.code} featured={plan.code === 'annual' ? true : false} buttonOnClick={e => changeSubscription(plan.code)}>
                              <Slot name='title'>{plan.name}</Slot>
                              {
                                If(plan.code === 'annual', () => (
                                  <Slot name='sticker'>Best Value!</Slot>
                                )).EndIf()
                              }
                              <Slot name='cost'>${plan.price} / Month</Slot>
                              <Slot name='description'>{plan.description}</Slot>
                              <Slot name='buttonText'>{currentSubscription === plan.code ? 'Subscribed!' : `${authState?.user?.hasGamePass ? 'Change' : 'Start'} Subscription`}</Slot>
                            </ProductPlanBox>
                          </Flex> 
                        ))
                      }
                    </>
                  )).Else(()=>(
                    <>
                      {
                        For(props.plans, (plan) => (
                          <Flex key={plan.name} fit>
                            <ProductPlanBox buttonHref={ `/subscribe/${plan.code}`} disabled={currentSubscription ===  plan.code} featured={plan.code === 'annual' ? true : false} >
                              <Slot name='title'>{plan.name}</Slot>
                              {
                                If(plan.code === 'annual', () => (
                                  <Slot name='sticker'>Best Value!</Slot>
                                )).EndIf()
                              }
                              <Slot name='cost'>{plan.price} / Month</Slot>
                              <Slot name='description'>{plan.description}</Slot>
                              <Slot name='buttonText'>{currentSubscription === plan.code ? 'Subscribed!' : `${authState?.user?.hasGamePass ? 'Change' : 'Start'} Subscription`}</Slot>
                            </ProductPlanBox>
                          </Flex> 
                        ))
                      }
                    </>
                  )).EndIf()
                }
              </Flex>
            </Block>
          )).EndIf()
        }

        <br />

        <Block>
          <Title className='--textCenter'>Why go premium?</Title>
          <Flex gap1 container collapseMobile className='GamePassPage__Features'>
            <Flex fit justifyCenter>
              <ProductFeature>
                <Slot name='graphic'>
                  <img src='/images/adfree.webp' alt='More Games' style={{ width:'65%' }} />
                </Slot>
                <Slot name='title'>Ad-Free</Slot>
                <Slot name='description'>Removed all banner and video ads on all games.</Slot>
              </ProductFeature>
            </Flex>
            <Flex fit justifyCenter>
              <ProductFeature>
                <Slot name='graphic'>
                  <img src='/images/devices.webp' alt='More Games' style={{ width:'60%' }} />
                </Slot>
                <Slot name='title'>All-Devices</Slot>
                <Slot name='description'>Play games on desktop, phones, tablets. All covered by one subscription.</Slot>
              </ProductFeature>
            </Flex>
            <Flex fit justifyCenter>
              <ProductFeature>
                <Slot name='graphic'>
                  <img src='/images/friends.webp' alt='More Games' style={{ width:'80%' }} />
                </Slot>
                <Slot name='title'>Everyone Plays</Slot>
                <Slot name='description'>Premium can be used by 5 different family members and friends.</Slot>
              </ProductFeature>
            </Flex>
          </Flex>
        </Block>

        <br />

        <Block>
          <Content large className='--textCenter'>
            <Title>About Addicting Games</Title>
            <p>
            Addicting Games is the largest online games site in the US. Reaching over 10 million unique users every month (comScore). We are considered founders in the casual game territory, developing and distributing innovative, irreverent, addictive online games since the early 2000s. Addicting Games has a long history of working closely with game developers to provide one of the largest ranges of free online games including action games, sports games, puzzle games, IO games, mobile games and many more. We release new games every Thursday and update features daily based on what's trending and what our users are telling us about.
            </p>
          </Content>
        </Block>
      
      </DefaultLayout>
    );
  });

